/**
 * Stable client identity for game-service socket connections.
 *
 * deviceId  — persistent UUID per browser profile (localStorage). Survives
 *             tabs, reloads and restarts; identifies the physical client.
 * sessionId — fresh UUID generated once per socket connection. The caller holds
 *             it for the life of that socket so it stays constant across
 *             socket.io reconnects, letting the server treat a reconnect as a
 *             re-attach of the same logical client rather than a brand-new one.
 *             Each socket (a new tab, or GamePlayClient vs SocketProvider in the
 *             same tab) gets its own sessionId.
 *
 * Replaces the hardcoded deviceId/sessionId literals that every client used to
 * send — those carried no information and made the server unable to tell one
 * connection from another.
 */
import uuid from 'react-native-uuid';

const DEVICE_ID_KEY = 'cc_device_id';

let cachedDeviceId: string | null = null;

/** Persistent per-browser-profile id. Generated once, then reused forever. */
export const getDeviceId = (): string => {
  if (cachedDeviceId) {
    return cachedDeviceId;
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const existing = window.localStorage.getItem(DEVICE_ID_KEY);
      if (existing) {
        cachedDeviceId = existing;
        return existing;
      }
      const generated = String(uuid.v4());
      window.localStorage.setItem(DEVICE_ID_KEY, generated);
      cachedDeviceId = generated;
      return generated;
    }
  } catch (error) {
    // localStorage unavailable (private mode / SSR) — fall through to an
    // in-memory id so the connection still carries a real value.
    console.warn('getDeviceId: localStorage unavailable', error);
  }
  if (!cachedDeviceId) {
    cachedDeviceId = String(uuid.v4());
  }
  return cachedDeviceId;
};

/** Fresh per-connection id. Call once per `cc_ws_client()` and keep the value. */
export const newSessionId = (): string => String(uuid.v4());
